<div class="container">

  <!-- Left Column - Builder Controls -->
  <div class="left-column">
    <div class="builder-panel" *ngIf="isDeploying || showDeploymentFinalMessage">
      <app-site-publishing
        [isDeploying]="isDeploying"
        [showDeploymentFinalMessage]="showDeploymentFinalMessage"
        [deploymentStatus]="deploymentStatus"
        [deploymentProgress]="deploymentProgress"
        [deploymentWebsiteUrl]="deploymentWebsiteUrl"
        [deploymentFinalMessage]="deploymentFinalMessage"
        [deploymentIconStatus]="deploymentIconStatus"
        [potentialWebsiteUrl]="potentialWebsiteUrl"
        [companyGuid]="companyGuid || ''"
        (backToEditMode)="onBackToEditMode()">
      </app-site-publishing>
    </div>
    <div class="builder-panel" *ngIf="!isDeploying && !showDeploymentFinalMessage">
      <div class="header-section">
        <h1 class="main-title">Let's create your website</h1>
        <p class="subtitle">Provide us with a little bit of information and background about your company, and we'll get started creating your website right away.</p>
      </div>

      <div class="progress-container" *ngIf="editMode === null || editMode === ''">
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="completionPercentage"></div>
        </div>
        <div class="progress-text">{{ completionPercentage }}% Complete</div>
      </div>

      <div class="form-sections">
        <!-- Website Theme Section -->
        <div class="form-section" [class.completed]="isThemeCompleted">
          <div class="section-header clickable" (click)="openSection('template')">
            <div class="section-title">
              <h3>Select Your <span class="section-name">Website's Theme</span></h3>
              <div class="completion-status" *ngIf="isThemeCompleted">
                <span class="complete-pill">Completed</span>
              </div>
            </div>
          </div>
          
          <!-- Theme content only shows when currently active -->
          <div class="section-content" *ngIf="currentActiveSection === 'template'">
            
            <!-- Original Template Form (when not in edit mode) -->
            <div *ngIf="!isEditingContent['template']">
              <div class="form-group">
                <label>Choose your website's template.</label>
                <div class="template-grid">
                  <div class="template-item" 
                       *ngFor="let template of templates" 
                       [class.selected]="formData.template === template.id"
                       (click)="onTemplateClick(template.id)">
                    <span class="template-name">{{ template.name }}</span>
                  </div>
                </div>
              </div>
              
              <div class="form-group">
                <label>Choose your website’s accent color</label>
                <div class="color-grid">
                  <div class="color-item" 
                       *ngFor="let color of getAvailableColors()" 
                       [class.selected]="formData.theme === color.key"
                       (click)="onColorClick(color.key)">
                    <div class="color-preview" [style.background]="color.theme.primary"></div>
                  </div>
                  <div class="color-item" 
                       [class.selected]="formData.theme === 'custom'"
                       (click)="openCustomColorPicker()">
                    <span class="color-name">Custom</span>
                  </div>
                </div>
              </div>
              

              
              <button 
                type="button" 
                class="btn complete-btn"
                [disabled]="isPreviewLoading || isThemeCompleting"
                [class.disabled]="isPreviewLoading"
                (click)="onCompleteTheme()">
                <span *ngIf="isThemeCompleting">Completing...</span>
                <span *ngIf="!isThemeCompleting">Complete Website Theme</span>
              </button>
            </div>

            <!-- V2 Inline Content Editor (when editing content) -->
            <app-inline-content-editor-v2
              *ngIf="isEditingContent['template']"
              [editRequest]="currentContentEditRequest['template']"
              [sectionTheme]="'template'"
              [currentSupplierData]="getCurrentSupplierData()"
              (contentUpdated)="onV2ContentUpdated($event, 'template')"
              (editCancelled)="onV2ContentEditCancelled('template')">
            </app-inline-content-editor-v2>


          </div>
        </div>

        <!-- About Us Section (moved to third position) -->
        <div class="form-section" [class.completed]="isAboutCompleted">
          <div class="section-header" 
               [class.clickable]="isThemeCompleted"
               (click)="isThemeCompleted ? openSection('about-us') : null">
            <div class="section-title">
              <h3>Generate Your <span class="section-name">'About Us'</span> Page</h3>
              <div class="completion-status" *ngIf="isAboutCompleted && isSectionImagesComplete('about-us')">
                <span class="complete-pill">Completed</span>
              </div>
              <button 
                type="button" 
                class="btn start-btn"
                *ngIf="!isAboutCompleted && isThemeCompleted && currentActiveSection !== 'about-us'"
                (click)="$event.stopPropagation(); openSection('about-us')">
                Start Now
              </button>
            </div>
          </div>
          
          <div class="section-content" *ngIf="currentActiveSection === 'about-us'">
            
            <!-- Original About Us Form (when not in edit mode) -->
            <div *ngIf="!isEditingContent['about-us']">
              <div class="form-group mb-0">
                <label for="aboutUs">About Us Content</label>
                <textarea 
                  id="aboutUs" 
                  [(ngModel)]="formData.aboutUs" 
                  (input)="onAboutUsChange($event)"
                  placeholder="Tell us about your company, history, and what makes you unique"
                  rows="6"
                  class="form-control"></textarea>
              </div>
              
              <div class="form-group">
                <button 
                  type="button" 
                  class="btn btn-secondary generate-btn"
                  [disabled]="!canGenerateAboutUsContent || isGeneratingAboutUsContent || isPreviewLoading "
                  [class.disabled]="!canGenerateAboutUsContent || isGeneratingAboutUsContent || isPreviewLoading"
                  (click)="onGenerateAboutUsContent()">
                  <span class="btn-icon sparkle-icon">
                    <span class="sparkle-triangle"></span>
                    <span class="sparkle-triangle"></span>
                  </span>
                  <span *ngIf="!isGeneratingAboutUsContent">Generate 'About Us' Content</span>
                  <span *ngIf="isGeneratingAboutUsContent">Generating...</span>
                </button>
              </div>
              
              <!-- Image Upload Buttons for About Us Section -->
              <div class="form-group" *ngIf="availableAboutUsImageTags.length > 0">
                <div class="image-upload-label">Upload the photos you want to display on your website’s About Us page.</div>
                <div class="image-upload-buttons">
                  <!-- APPROACH 1: Direct Method Call (Recommended) -->
                  <div 
                    *ngFor="let tagName of availableAboutUsImageTags;let index = index" 
                    class="image-upload-item">
                    <span class="image-label">Image {{ index + 1 }}</span>
                    <button 
                      type="button" 
                      class="btn choose-file-btn"
                      [disabled]="isPreviewLoading || isImageUploading(tagName)"
                      [class.uploading]="isImageUploading(tagName)"
                      (click)="triggerImageUploaderForTag(tagName)"
                      >
                      <span *ngIf="!isImageUploading(tagName)">Choose File</span>
                      <span *ngIf="isImageUploading(tagName)" class="uploading-content">
                        Uploading...
                      </span>
                    </button>
                    <span class="upload-status" [class.uploaded]="isImageUploaded(tagName)" [class.not-uploaded]="!isImageUploaded(tagName)">
                      {{ getImageDisplayText(tagName) }}
                    </span>
                  </div>
                  
                  <!-- APPROACH 4: HTML Parsing (Alternative) - Uncomment to use -->
                  <!-- 
                  <button 
                    *ngFor="let tagName of getAvailableImageTagNames()" 
                    type="button" 
                    class="btn btn-outline-secondary image-upload-btn"
                    [disabled]="isPreviewLoading"
                    (click)="triggerImageUploaderForTagFromHTML(tagName)"
                    [title]="'Upload ' + tagName.replace('IMG_', '').replace('_', ' ').toLowerCase() + ' (HTML)'">
                    <span class="button-icon">🔍</span>
                    <span class="button-text">{{ tagName.replace('IMG_', '').replace('_', ' ') }} (HTML)</span>
                  </button>
                  -->
                </div>
              </div>
              
              <button 
                type="button" 
                class="btn complete-btn"
                [disabled]="!isAboutGenerated || isPreviewLoading || isAboutCompleting || !isSectionImagesComplete('about-us')"
                [class.disabled]="!isAboutGenerated || isPreviewLoading || isAboutCompleting || !isSectionImagesComplete('about-us')"
                (click)="onCompleteAboutUs()">
                <span *ngIf="isAboutCompleting">Completing...</span>
                <span *ngIf="!isAboutCompleting">Complete 'About Us' Page</span>
              </button>
            </div>

            <!-- V2 Inline Content Editor (when editing content) -->
            <app-inline-content-editor-v2
              *ngIf="isEditingContent['about-us']"
              [editRequest]="currentContentEditRequest['about-us']"
              [sectionTheme]="'about-us'"
              [currentSupplierData]="getCurrentSupplierData()"
              (contentUpdated)="onV2ContentUpdated($event, 'about-us')"
              (editCancelled)="onV2ContentEditCancelled('about-us')">
            </app-inline-content-editor-v2>


          </div>
        </div>

        <!-- Capabilities Section (moved to second position) -->
        <div class="form-section" [class.completed]="isCapabilitiesCompleted">
          <div class="section-header" 
               [class.clickable]="isAboutCompleted && isSectionImagesComplete('about-us')"
               (click)="isAboutCompleted && isSectionImagesComplete('about-us') ? openSection('capabilities') : null">
            <div class="section-title">
              <h3>Generate Your <span class="section-name">'Capabilities'</span> Page</h3>
              <div class="completion-status" *ngIf="isCapabilitiesCompleted && isSectionImagesComplete('capabilities')">
                <span class="complete-pill">Completed</span>
              </div>
              <button 
                type="button" 
                class="btn start-btn"
                *ngIf="isAboutCompleted && !isCapabilitiesCompleted && currentActiveSection !== 'capabilities' && isSectionImagesComplete('about-us')"
                (click)="$event.stopPropagation(); openSection('capabilities')">
                Start Now
              </button>
            </div>
          </div>
          
          <!-- Capabilities content shows when currently active -->
          <div class="section-content" *ngIf="currentActiveSection === 'capabilities'">
            
            <!-- Original Capabilities Form (when not in edit mode) -->
            <div *ngIf="!isEditingContent['capabilities']">
              <div class="capabilities-intro">
                <p class="section-description">Select all of the capabilities that your shop covers.</p>
              </div>
              
              <div class="form-group">
                <div class="multiselect-container" (click)="$event.stopPropagation()">
                  <div class="multiselect-display" (click)="toggleCapabilitiesDropdown()">
                    <span class="selected-items" *ngIf="formData.capabilities.length > 0">
                      {{ formData.capabilities.join(', ') }}
                    </span>
                    <span class="placeholder" *ngIf="formData.capabilities.length === 0">
                      Select capabilities...
                    </span>
                    <span class="dropdown-arrow">
                      <img src="assets/icon/down-arrow.svg" alt="dropdown-arrow">
                    </span>
                  </div>
                  
                  <div class="multiselect-dropdown" *ngIf="isCapabilitiesDropdownOpen" (click)="$event.stopPropagation()">
                    <div class="dropdown-options">
                      <div class="capability-option" 
                           *ngFor="let capability of capabilitiesOptions"
                           [class.selected]="isCapabilitySelected(capability)"
                           (click)="toggleCapability(capability)">
                        <div class="checkbox">
                          <div class="checkmark" *ngIf="isCapabilitySelected(capability)">✓</div>
                        </div>
                        <span>{{ capability }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="form-group mb-0">
                <label class="field-label">Describe any additional capabilities that we missed.</label>
                <div class="textarea-wrapper">
                  <textarea 
                    id="additionalCapabilities" 
                    [(ngModel)]="formData.additionalCapabilities" 
                    (input)="onAdditionalCapabilitiesChange($event)"
                    placeholder="Please add any additional services or capabilities your company offers, but were not covered in the list above (e.g., Powder Coating, MIG & TIG Welding, Grinding, Heat Treating)"
                    rows="4"
                    maxlength="300"
                    class="form-control"></textarea>
                  <div class="character-count">{{ formData.additionalCapabilities.length }}/300</div>
                </div>
              </div>
              
              <div class="form-group mb-0">
                <label class="field-label">List out all of your shop's equipment.</label>
                <div class="textarea-wrapper">
                  <textarea 
                    id="equipment" 
                    [(ngModel)]="formData.equipment" 
                    (input)="onEquipmentChange($event)"
                    placeholder="Describe your equipment and machinery"
                    rows="4"
                    maxlength="600"
                    class="form-control"></textarea>
                  <div class="character-count">{{ formData.equipment.length }}/600</div>
                </div>
              </div>
              
              <div class="form-group">
                <button 
                  type="button" 
                  class="btn generate-btn"
                  [disabled]="!canGenerateContent || isGeneratingContent || isPreviewLoading"
                  [class.disabled]="!canGenerateContent || isGeneratingContent || isPreviewLoading"
                  (click)="onGenerateCapabilitiesContent()">
                  <span class="btn-icon sparkle-icon">
                    <span class="sparkle-triangle"></span>
                    <span class="sparkle-triangle"></span>
                  </span>
                  <span *ngIf="isGeneratingContent">Generating...</span>
                  <span *ngIf="!isGeneratingContent">Generate 'Capabilities' Content</span>
                </button>
              </div>

              <!-- Image Upload Buttons for Capabilities Section -->
              <div class="form-group" *ngIf="availableCapabilitiesImageTags.length > 0">
                <div class="image-upload-label">Upload the photo(s) you want to display on your website’s Capabilites page.</div>
                <div class="image-upload-buttons">
                  <!-- APPROACH 1: Direct Method Call (Recommended) -->
                  <div 
                    *ngFor="let tagName of availableCapabilitiesImageTags; let index = index" 
                    class="image-upload-item">
                    <span class="image-label">Image {{ index + 1 }}</span>
                    <button 
                      type="button" 
                      class="btn choose-file-btn"
                      [disabled]="isPreviewLoading || isImageUploading(tagName)"
                      [class.uploading]="isImageUploading(tagName)"
                      (click)="triggerImageUploaderForTag(tagName)"
                      >
                      <span *ngIf="!isImageUploading(tagName)">Choose File</span>
                      <span *ngIf="isImageUploading(tagName)" class="uploading-content">
                        Uploading...
                      </span>
                    </button>
                    <span class="upload-status" [class.uploaded]="isImageUploaded(tagName)" [class.not-uploaded]="!isImageUploaded(tagName)">
                      {{ getImageDisplayText(tagName) }}
                    </span>
                  </div>
                </div>
              </div>
              
              
              <button 
                type="button" 
                class="btn btn-primary complete-btn"
                [disabled]="!isCapabilitiesGenerated || isPreviewLoading || isCapabilitiesCompleting || !isSectionImagesComplete('capabilities')"
                [class.disabled]="!isCapabilitiesGenerated || isPreviewLoading || isCapabilitiesCompleting || !isSectionImagesComplete('capabilities')"
                (click)="onCompleteCapabilities()">
                <span *ngIf="isCapabilitiesCompleting">Completing...</span>
                <span *ngIf="!isCapabilitiesCompleting">Complete 'Capabilities' Page</span>
                
              </button>
            </div>

            <!-- V2 Inline Content Editor (when editing content) -->
            <app-inline-content-editor-v2
              *ngIf="isEditingContent['capabilities']"
              [editRequest]="currentContentEditRequest['capabilities']"
              [sectionTheme]="'capabilities'"
              [currentSupplierData]="getCurrentSupplierData()"
              (contentUpdated)="onV2ContentUpdated($event, 'capabilities')"
              (editCancelled)="onV2ContentEditCancelled('capabilities')">
            </app-inline-content-editor-v2>


          </div>
        </div>

        <!-- Contact Us Section -->
        <div class="form-section" [class.completed]="isContactCompleted">
          <div class="section-header" 
               [class.clickable]="isCapabilitiesCompleted"
               (click)="isCapabilitiesCompleted ? openSection('contact') : null">
            <div class="section-title">
              <h3>Generate Your <span class="section-name">'Contact Us'</span> Page</h3>
              <div class="completion-status" *ngIf="isContactCompleted">
                <span class="complete-pill">Completed</span>
              </div>
              <button 
                type="button" 
                class="btn start-btn"
                *ngIf="isCapabilitiesCompleted && !isContactCompleted && currentActiveSection !== 'contact'"
                (click)="$event.stopPropagation(); openSection('contact')">
                Start Now
              </button>
            </div>
          </div>
          
          <div class="section-content" *ngIf="currentActiveSection === 'contact'">
            
            <!-- Original Contact Form (when not in edit mode) -->
            <div *ngIf="!isEditingContent['contact']" class="contact-form">
              <div class="contact-form-header">
                <h3>Add your company's contact information.</h3>
              </div>
              
              <!-- First row: Email and Phone side by side -->
              <div class="form-row">
                <div class="form-group half-width">
                  <input 
                    type="email" 
                    id="email" 
                    [(ngModel)]="formData.contact.email" 
                    (input)="onContactFieldChange('email', $event)"
                    placeholder="Email *"
                    [class.error]="contactFieldErrors['email']"
                    class="form-control">
                </div>
                
                <div class="form-group half-width">
                  <input 
                    type="tel" 
                    id="phone" 
                    [(ngModel)]="formData.contact.phone" 
                    (input)="onContactFieldChange('phone', $event)"
                    placeholder="Phone (10 digits) *"
                    maxlength="10"
                    [class.error]="contactFieldErrors['phone']"
                    class="form-control">
                </div>
              </div>
              
              <!-- Second row: Street Address full width -->
              <div class="form-row">
                <div class="form-group full-width">
                  <input 
                    type="text" 
                    id="streetAddress" 
                    [(ngModel)]="formData.contact.streetAddress" 
                    (input)="onContactFieldChange('streetAddress', $event)"
                    placeholder="Street Address *"
                    [class.error]="contactFieldErrors['streetAddress']"
                    class="form-control">
                </div>
              </div>
              
              <!-- Third row: City, State, Zip Code in three columns -->
              <div class="form-row">
                <div class="form-group third-width">
                  <input 
                    type="text" 
                    id="city" 
                    [(ngModel)]="formData.contact.city" 
                    (input)="onContactFieldChange('city', $event)"
                    placeholder="City *"
                    [class.error]="contactFieldErrors['city']"
                    class="form-control">
                </div>
                
                <div class="form-group third-width">
                  <input 
                    type="text" 
                    id="state" 
                    [(ngModel)]="formData.contact.state" 
                    (input)="onContactFieldChange('state', $event)"
                    placeholder="State *"
                    [class.error]="contactFieldErrors['state']"
                    class="form-control">
                </div>
                
                <div class="form-group third-width">
                  <input 
                    type="text" 
                    id="zipCode" 
                    [(ngModel)]="formData.contact.zipCode" 
                    (input)="onContactFieldChange('zipCode', $event)"
                    placeholder="Zip Code *"
                    [class.error]="contactFieldErrors['zipCode']"
                    class="form-control">
                </div>
              </div>
              
              <button 
                type="button" 
                class="btn complete-btn"
                [disabled]="isPreviewLoading || isContactCompleting"
                [class.disabled]="isPreviewLoading || isContactCompleting"
                (click)="onCompleteContact()">
                <span *ngIf="isContactCompleting">Completing...</span>
                <span *ngIf="!isContactCompleting">Complete 'Contact Us' Page</span>
              </button>
            </div>

            <!-- V2 Inline Content Editor (when editing content) -->
            <app-inline-content-editor-v2
              *ngIf="isEditingContent['contact']"
              [editRequest]="currentContentEditRequest['contact']"
              [sectionTheme]="'contact'"
              [currentSupplierData]="getCurrentSupplierData()"
              (contentUpdated)="onV2ContentUpdated($event, 'contact')"
              (editCancelled)="onV2ContentEditCancelled('contact')">
            </app-inline-content-editor-v2>


          </div>
        </div>

        <!-- Company Logo Section -->
        <div class="form-section" [class.completed]="isLogoCompleted">
          <div class="section-header"
               [class.clickable]="isCapabilitiesCompleted"
               (click)="(isCapabilitiesCompleted ) ? openSection('logo') : null">
            <div class="section-title">
              <h3>Review Your <span class="section-name">'Home' </span>Page Content</h3>
              <div class="completion-status" *ngIf="isLogoCompleted">
                <span class="complete-pill">Completed</span>
              </div>
              <button 
                type="button" 
                class="btn start-btn"
                *ngIf="(isCapabilitiesCompleted) && !isLogoCompleted"
                (click)="$event.stopPropagation(); openSection('logo')">
                Start Now
              </button>
            </div>
          </div>
          
          <!-- Image Upload Buttons for Logo Section -->
          <div class="section-content" *ngIf="currentActiveSection === 'logo'">
            <div class="form-group" *ngIf="availableLogoImageTags.length > 0 && !isEditingContent['logo']">
              <!-- <label class="image-upload-label">Upload Images</label> -->
              <div class="image-upload-buttons image-upload-section">
                <!-- APPROACH 1: Direct Method Call (Recommended) -->
                <div 
                  *ngFor="let tagName of availableLogoImageTags;let index = index" 
                  class="image-upload-item">
                  <span class="image-label">{{ tagName === 'IMG_COMPANY_LOGO' ? 'Company Logo' : 'Image ' + (index) }}</span>
                  <button 
                    type="button" 
                    class="btn choose-file-btn"
                    [disabled]="isPreviewLoading || isImageUploading(tagName)"
                    [class.uploading]="isImageUploading(tagName)"
                    (click)="triggerImageUploaderForTag(tagName)"
                    >
                    <span *ngIf="!isImageUploading(tagName)">Choose File</span>
                    <span *ngIf="isImageUploading(tagName)" class="uploading-content">
                      Uploading...
                    </span>
                  </button>
                  <span class="upload-status" [class.uploaded]="isImageUploaded(tagName)" [class.not-uploaded]="!isImageUploaded(tagName)">
                    {{ getImageDisplayText(tagName) }}
                  </span>
                </div>
              </div>
              <button 
                type="button" 
                class="btn btn-primary complete-btn"
                [disabled]="isPreviewLoading || isLogoCompleting || !isSectionImagesComplete('logo')"
                [class.disabled]="isPreviewLoading || isLogoCompleting || !isSectionImagesComplete('logo')"
                (click)="onCompleteLogoSection()">
                <span *ngIf="isLogoCompleting">Completing...</span>
                <span *ngIf="!isLogoCompleting">Complete 'Home' Page</span>
              </button>
            </div>


            <!-- V2 Inline Content Editor (when editing content) -->
            <app-inline-content-editor-v2
              *ngIf="isEditingContent['logo']"
              [editRequest]="currentContentEditRequest['logo']"
              [sectionTheme]="'logo'"
              [currentSupplierData]="getCurrentSupplierData()"
              (contentUpdated)="onV2ContentUpdated($event, 'logo')"
              (editCancelled)="onV2ContentEditCancelled('logo')">
            </app-inline-content-editor-v2>


          </div>

        </div>
      </div>
    </div>
    
    <!-- Action Buttons -->
    <div class="action-buttons" *ngIf="!isDeploying && !showDeploymentFinalMessage">
      <div class="save-section">
        <button 
          class="save-btn" 
          [disabled]="isSavingForLater || !companyGuid"
          [class.saving]="isSavingForLater"
          (click)="onSaveForLater()">
          <span *ngIf="!isSavingForLater">Save for Later</span>
          <span *ngIf="isSavingForLater">Saving...</span>
        </button>
      </div>
      <button 
        class="publish-btn" 
        [disabled]="!isAllSectionsCompleted() || isPreviewLoading || isDeploying || isSavingForLater || isPublishButtonLoading"
        [class.disabled]="!isAllSectionsCompleted() || isPreviewLoading || isDeploying || isSavingForLater || isPublishButtonLoading"
        (click)="onPublishSite()">
        <span *ngIf="!isDeploying && !isPublishButtonLoading">Publish Site</span>
        <span *ngIf="isPublishButtonLoading">Preparing...</span>
        <span *ngIf="isDeploying">Publishing...</span>
      </button>
    </div>
  </div>

  <!-- Right Column - Preview -->
  <div class="preview-panel">
    <div class="preview-container">
      <!-- Website Template Preview -->
      <app-form-builder-preview
        #websitePreview
        [supplierData]="getCurrentSupplierData()"
        [templateId]="formData.template"
        [themeKey]="formData.theme"
        (openInlineEditor)="openInlineEditor($event)"
        (previewLoadingStateChanged)="onPreviewLoadingStateChanged($event)">
      </app-form-builder-preview>
    </div>
  </div>

  <!-- Custom Color Picker Modal -->
  <div class="custom-color-modal" *ngIf="showCustomColorPicker">
    <div class="modal-overlay" (click)="closeCustomColorPicker()"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h3>Custom Color Theme</h3>
        <button class="close-btn" (click)="closeCustomColorPicker()">×</button>
      </div>
      <div class="modal-body">
        <div class="color-picker-section">
          <label>Primary Color</label>
          <div class="color-input-group" (click)="openColorPicker()">
            <input 
              #colorInput
              type="color" 
              [(ngModel)]="customThemeColor" 
              (change)="onCustomColorChange()"
              class="color-input">
            <input 
              type="text" 
              [(ngModel)]="customThemeColor" 
              (input)="onCustomColorTextChange()"
              placeholder="#2e58d6"
              class="color-text-input">
          </div>
        </div>
        <div class="color-preview-section">
          <label>Preview</label>
          <div class="theme-preview">
            <div class="preview-button" [style.background]="customThemeColor">Sample Button</div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeCustomColorPicker()">Cancel</button>
        <button class="btn btn-primary" (click)="applyCustomColor()">Apply Color</button>
      </div>
    </div>
  </div>

  <!-- AI Content Generation Overlay -->
  <div class="ai-generation-overlay" *ngIf="isGeneratingContent">
    <div class="ai-generation-modal">
      <div class="ai-generation-content">
        <div class="ai-generation-icon">
          <div class="box">
            <div class="spinner"></div>
          </div>
        </div>
        <h3 class="ai-generation-title">Generating Your Website Content</h3>
        <p class="ai-generation-description">OTTO is creating personalized content for your website based on your business information.</p>
        
        <!-- Progress Bar -->
        <div class="ai-progress-container">
          <div class="ai-progress-bar">
            <div class="ai-progress-fill"></div>
          </div>
          <div class="ai-progress-text">This may take a few moments...</div>
        </div>
        
        <!-- Loading Animation -->
        <div class="ai-loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>
  </div>

                  <!-- Deployment Progress Overlay -->
                <!-- <div class="deployment-overlay" *ngIf="isDeploying || showDeploymentFinalMessage">
                  <div class="deployment-modal">
                    <div class="deployment-content">
                      <div class="deployment-icon">
                        <div class="box">
                          <div class="spinner" *ngIf="deploymentIconStatus === 'loading'"></div>
                          <div *ngIf="deploymentIconStatus === 'success'" class="success-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" fill="#4CAF50" stroke="#4CAF50" stroke-width="2"/>
                              <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </div>
                          <div *ngIf="deploymentIconStatus === 'error'" class="error-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" fill="#F44336" stroke="#F44336" stroke-width="2"/>
                              <path d="M15 9l-6 6M9 9l6 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div *ngIf="showDeploymentFinalMessage" class="deployment-final-message">
                        <h3 class="deployment-title">Deployment Complete</h3>
                        <p>It might take a 3-5 minutes for the website to reflect all the changes.</p>
                        <p class="deployment-description">
                          {{ deploymentFinalMessage }}
                        </p>
                        <div *ngIf="deploymentWebsiteUrl" class="website-url-container">
                          <a [href]="deploymentWebsiteUrl" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             class="website-link">
                            {{ deploymentWebsiteUrl }}
                          </a>
                        </div>
                      </div>
                      
                      <div *ngIf="!showDeploymentFinalMessage">
                        <h3 class="deployment-title">Publishing Your Website</h3>
                        <p class="deployment-description">{{ deploymentStatus }}</p>
                        
                        <div class="deployment-progress-container">
                          <div class="deployment-progress-bar">
                            <div class="deployment-progress-fill" [style.width.%]="deploymentProgress"></div>
                          </div>
                          <div class="deployment-progress-text">{{ deploymentProgress }}% Complete</div>
                        </div>
                        
                        <div class="deployment-loading-dots">
                          <span class="dot"></span>
                          <span class="dot"></span>
                          <span class="dot"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> -->

  <!-- About Us AI Content Generation Overlay -->
  <div class="ai-generation-overlay" *ngIf="isGeneratingAboutUsContent">
    <div class="ai-generation-modal">
      <div class="ai-generation-content">
        <div class="ai-generation-icon">
          <div class="otto-logo-loader">
            <div class="box">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
        <h3 class="ai-generation-title">Generating Your About Us Content</h3>
        <p class="ai-generation-description">OTTO is creating personalized About Us content for your website based on your business information.</p>
        
        <!-- Progress Bar -->
        <div class="ai-progress-container">
          <div class="ai-progress-bar">
            <div class="ai-progress-fill"></div>
          </div>
          <div class="ai-progress-text">This may take a few moments...</div>
        </div>
        
        <!-- Loading Animation -->
        <div class="ai-loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>
  </div>

  <!-- Warning Popup -->
  <app-warning-popup 
    [isVisible]="showWarningPopup"
    [message]="warningMessage"
    [showBackToDashboard]="true">
  </app-warning-popup>


</div> 